import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { editorTheme } from '../../theme';

const Field = styled.label<{ disabled?: boolean }>`
  height: 32px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px;
  border: 1px solid ${editorTheme.border};
  border-radius: 6px;
  background: ${({ disabled }) =>
    disabled ? editorTheme.surfaceMuted : editorTheme.surface};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  &:focus-within {
    border-color: ${editorTheme.accent};
  }
`;

const Prefix = styled.span`
  flex: none;
  font-size: 11px;
  font-weight: 500;
  color: ${editorTheme.textMuted};
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 12px;
  color: ${editorTheme.text};
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

interface NumberFieldProps {
  label: string;
  prefix: string;
  value: number | null;
  onCommit: (value: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  decimals?: number;
}

const round = (value: number, decimals: number) => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

const format = (value: number | null, decimals: number) =>
  value === null ? '' : String(round(value, decimals));

// Edits are applied on Enter or blur, not per keystroke: every applied
// change is an undo step and an autosave.
const NumberField: FC<NumberFieldProps> = ({
  label,
  prefix,
  value,
  onCommit,
  disabled,
  min,
  max,
  decimals = 0,
}) => {
  const [draft, setDraft] = useState(format(value, decimals));
  const [focused, setFocused] = useState(false);
  const cancelled = useRef(false);

  useEffect(() => {
    if (!focused) setDraft(format(value, decimals));
  }, [value, focused, decimals]);

  const commit = () => {
    const parsed = Number(draft);
    if (draft.trim() === '' || Number.isNaN(parsed)) {
      setDraft(format(value, decimals));
      return;
    }
    let next = parsed;
    if (min !== undefined) next = Math.max(min, next);
    if (max !== undefined) next = Math.min(max, next);
    if (value === null || round(next, decimals) !== round(value, decimals)) {
      onCommit(next);
    }
    setDraft(format(next, decimals));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    } else if (event.key === 'Escape') {
      cancelled.current = true;
      setDraft(format(value, decimals));
      event.currentTarget.blur();
    }
  };

  return (
    <Field disabled={disabled}>
      <Prefix aria-hidden="true">{prefix}</Prefix>
      <Input
        type="number"
        inputMode={decimals ? 'decimal' : 'numeric'}
        step={10 ** -decimals}
        aria-label={label}
        value={draft}
        disabled={disabled}
        onChange={(event) => setDraft(event.target.value)}
        onFocus={(event) => {
          setFocused(true);
          event.target.select();
        }}
        onBlur={() => {
          setFocused(false);
          if (cancelled.current) {
            cancelled.current = false;
            return;
          }
          commit();
        }}
        onKeyDown={onKeyDown}
      />
    </Field>
  );
};

export default NumberField;
